// Mock availability store. Mirrors the NetSuite custom record
// `customrecord_mr_b2b_availability` (one row per variant SKU per location).
// This is the B2B subset of stock — independent of the item's own
// availability — and gets decremented as orders are placed via the B2B.
//
// Two warehouses: US (Bozeman, MT) and INTL (Vietnam DC). Each carries
// its own price (USD vs an INTL list price that might differ for duties)
// and its own stock count.

export const WAREHOUSES = [
  { id: "US",   label: "US Warehouse",            short: "USA",  flag: "🇺🇸", currency: "USD" },
  { id: "INTL", label: "International Warehouse", short: "INTL", flag: "🌎", currency: "USD" },
];

const STOCK_US = {
  "RATS-BLACK-S": 12, "RATS-BLACK-M": 8, "RATS-BLACK-L": 4,
  "RATS-COYOTE-S": 6, "RATS-COYOTE-M": 9, "RATS-COYOTE-L": 3,
  "RATS-MULTICAM-S": 0, "RATS-MULTICAM-M": 5, "RATS-MULTICAM-L": 2,
  "3DAB-COYOTE-S": 20, "3DAB-COYOTE-M": 18, "3DAB-COYOTE-L": 11,
  "3DAB-MULTICAM-S": 7, "3DAB-MULTICAM-M": 4, "3DAB-MULTICAM-L": 0,
  "BJ100-COYOTE-M": 6, "BJ100-COYOTE-L": 3,
  "BJ100-MULTICAM-M": 2, "BJ100-MULTICAM-L": 1,
  "MLF-COYOTE": 14,
  "FIREP-YELLOW-M": 22, "FIREP-YELLOW-L": 18,
  "FIREP-BLACK-M": 9, "FIREP-BLACK-L": 7,
  "MET50-OLIVE-S": 8, "MET50-OLIVE-M": 12, "MET50-OLIVE-L": 6,
  "MET50-STONE-S": 4, "MET50-STONE-M": 5, "MET50-STONE-L": 2,
  "EDC2D-BLACK": 41, "EDC2D-SHADOW": 19, "EDC2D-SAND": 12,
  "SCREE32-BLACK": 18, "SCREE32-SAND": 11,
  "LIDP-BLACK": 60, "LIDP-COYOTE": 44,
};

const STOCK_INTL = {
  "RATS-BLACK-S": 30, "RATS-BLACK-M": 22, "RATS-BLACK-L": 11,
  "RATS-COYOTE-S": 18, "RATS-COYOTE-M": 16, "RATS-COYOTE-L": 9,
  "RATS-MULTICAM-S": 6, "RATS-MULTICAM-M": 12, "RATS-MULTICAM-L": 4,
  "3DAB-COYOTE-S": 40, "3DAB-COYOTE-M": 33, "3DAB-COYOTE-L": 22,
  "3DAB-MULTICAM-S": 14, "3DAB-MULTICAM-M": 10, "3DAB-MULTICAM-L": 5,
  "BJ100-COYOTE-M": 12, "BJ100-COYOTE-L": 7,
  "BJ100-MULTICAM-M": 4, "BJ100-MULTICAM-L": 2,
  "MLF-COYOTE": 28,
  "FIREP-YELLOW-M": 14, "FIREP-YELLOW-L": 11,
  "FIREP-BLACK-M": 6, "FIREP-BLACK-L": 4,
  "MET50-OLIVE-S": 16, "MET50-OLIVE-M": 24, "MET50-OLIVE-L": 12,
  "MET50-STONE-S": 9, "MET50-STONE-M": 11, "MET50-STONE-L": 5,
  "EDC2D-BLACK": 80, "EDC2D-SHADOW": 38, "EDC2D-SAND": 24,
  "SCREE32-BLACK": 36, "SCREE32-SAND": 22,
  "LIDP-BLACK": 110, "LIDP-COYOTE": 92,
};

// Public-mode numbers stand in for retail-network availability (legacy).
const PUBLIC_STOCK = {
  "RATS-BLACK-S": 60, "RATS-BLACK-M": 45, "RATS-BLACK-L": 22,
  "RATS-COYOTE-S": 38, "RATS-COYOTE-M": 41, "RATS-COYOTE-L": 18,
  "MLF-COYOTE": 52,
};

// US/INTL prices may differ — INTL gets a small uplift to cover duties.
const INTL_UPLIFT = 1.08;

export function mockAvailability(sku, mode) {
  if (mode === "public") {
    const qty = PUBLIC_STOCK[sku];
    return {
      sku,
      available: typeof qty === "number" ? qty : null,
      location: "RETAIL-NETWORK",
      mode: "public",
    };
  }
  const us = STOCK_US[sku];
  const intl = STOCK_INTL[sku];
  const total = (us || 0) + (intl || 0);
  return {
    sku,
    available: total || null,
    location: "B2B-WAREHOUSES",
    mode: "b2b",
    warehouses: [
      { id: "US",   label: "US Warehouse",            available: typeof us   === "number" ? us   : null },
      { id: "INTL", label: "International Warehouse", available: typeof intl === "number" ? intl : null },
    ],
  };
}

// Per-warehouse price for a given variant. NetSuite will return prices
// per-location once the B2B inventory + price list is wired up; for now
// we apply a flat uplift and return the same currency.
export function mockWarehousePrices({ basePrice, currency = "USD" } = {}) {
  const base = Number(basePrice) || 0;
  return {
    US:   { amount: base, currency },
    INTL: { amount: Math.round(base * INTL_UPLIFT * 100) / 100, currency },
  };
}
